import React, { useState, useEffect, useRef } from "react";
import { Terminal as XTerm } from "xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { FaTimes, FaExpand, FaCompress, FaTrash } from "react-icons/fa";
import "xterm/css/xterm.css";
import "../styles/Terminal.css";

const Terminal = ({ profileUuid, isOpen, onClose }) => {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [terminalWidth, setTerminalWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !profileUuid) return;

    // Initialize xterm.js terminal
    const term = new XTerm({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: "#000000",
        foreground: "#ffffff",
        cursor: "#9F88FF",
        cursorAccent: "#000000",
        selection: "rgba(159, 136, 255, 0.3)",
        black: "#000000",
        red: "#e74c3c",
        green: "#2ecc71",
        yellow: "#f39c12",
        blue: "#3498db",
        magenta: "#9b59b6",
        cyan: "#1abc9c",
        white: "#ecf0f1",
        brightBlack: "#34495e",
        brightRed: "#ff6b6b",
        brightGreen: "#4ecdc4",
        brightYellow: "#ffe66d",
        brightBlue: "#5dade2",
        brightMagenta: "#9F88FF",
        brightCyan: "#48dbfb",
        brightWhite: "#ffffff",
      },
      scrollback: 1000,
      tabStopWidth: 4,
    });

    // Add fit addon for responsive terminal
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    fitAddonRef.current = fitAddon;

    // Add web links addon (makes URLs clickable)
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(webLinksAddon);

    // Open terminal in the DOM
    if (terminalRef.current) {
      term.open(terminalRef.current);
      fitAddon.fit();
      xtermRef.current = term;

      // Welcome message
      term.writeln("\x1b[1;34m╔══════════════════════════════════════════════════════════╗\x1b[0m");
      term.writeln("\x1b[1;34m║\x1b[0m  \x1b[1;35mTriagz Remote Console\x1b[0m                                   \x1b[1;34m║\x1b[0m");
      term.writeln("\x1b[1;34m║\x1b[0m  Connecting to agent...                                  \x1b[1;34m║\x1b[0m");
      term.writeln("\x1b[1;34m╚══════════════════════════════════════════════════════════╝\x1b[0m");
      term.writeln("");

      // Connect to WebSocket
      connectWebSocket(term, profileUuid);
    }

    // Handle terminal resize
    const handleResize = () => {
      if (fitAddonRef.current && xtermRef.current) {
        fitAddonRef.current.fit();
        // Send resize info to backend
        const dimensions = {
          cols: xtermRef.current.cols,
          rows: xtermRef.current.rows,
        };
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "terminal_resize",
              data: dimensions,
            })
          );
        }
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (xtermRef.current) {
        xtermRef.current.dispose();
      }
    };
  }, [isOpen, profileUuid]);

  const connectWebSocket = (term, profileUuid) => {
    try {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/ws/terminal/${profileUuid}/`;
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError("");
        term.writeln("\x1b[1;32m✓ Connected to agent Console\x1b[0m");
        term.writeln("");

        // Send initial terminal size
        const dimensions = {
          cols: term.cols,
          rows: term.rows,
        };
        ws.send(
          JSON.stringify({
            type: "terminal_start",
            data: dimensions,
          })
        );

        // Handle terminal input
        term.onData((data) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                type: "terminal_input",
                data: data,
              })
            );
          }
        });
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === "terminal_output") {
            term.write(message.data);
          } else if (message.type === "terminal_error") {
            term.writeln(`\x1b[1;31mError: ${message.data}\x1b[0m`);
          } else if (message.type === "terminal_closed") {
            term.writeln("\x1b[1;33m\r\n[Terminal session closed]\x1b[0m");
            setIsConnected(false);
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setError("Failed to connect to Console");
        term.writeln("\x1b[1;31m✗ Connection error\x1b[0m");
        setIsConnected(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        term.writeln("\x1b[1;33m\r\n[Connection closed]\x1b[0m");
      };
    } catch (err) {
      console.error("Error creating WebSocket:", err);
      setError("Failed to create WebSocket connection");
    }
  };

  const handleClear = () => {
    if (xtermRef.current) {
      xtermRef.current.clear();
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    // Delay fit to allow CSS transition to complete
    setTimeout(() => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    }, 300);
  };

  // Handle terminal resize by dragging
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.classList.add('resizing');
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      // Calculate new width (terminal is on the right, so we resize from the left edge)
      const newWidth = window.innerWidth - e.clientX;
      
      // Define min and max widths
      const minWidth = 400;
      const maxWidth = window.innerWidth - 100; // Leave 100px for the chat section
      
      // Constrain the width
      const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      setTerminalWidth(constrainedWidth);
      
      // Trigger terminal fit after resize
      if (fitAddonRef.current) {
        setTimeout(() => {
          fitAddonRef.current.fit();
        }, 10);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.classList.remove('resizing');
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove('resizing');
    };
  }, [isResizing]);

  if (!isOpen) return null;

  return (
    <div 
      className={`terminal-sidebar ${isFullscreen ? "fullscreen" : ""}`}
      style={!isFullscreen ? { width: `${terminalWidth}px` } : {}}
    >
      <div 
        className="terminal-resize-handle"
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      />
      <div className="terminal-header">
        <div className="terminal-title">
          <h3>Console</h3>
          {isConnected && (
            <span className="connection-status connected">Connected</span>
          )}
          {!isConnected && error && (
            <span className="connection-status disconnected">Disconnected</span>
          )}
        </div>
        <div className="terminal-actions">
          <button
            className="terminal-action-btn"
            onClick={handleClear}
            title="Clear terminal"
          >
            <FaTrash />
          </button>
          <button
            className="terminal-action-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
          <button className="terminal-action-btn close" onClick={onClose} title="Close terminal">
            <FaTimes />
          </button>
        </div>
      </div>

      {error && (
        <div className="terminal-error">
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className="terminal-container">
        <div ref={terminalRef} className="terminal-instance" />
      </div>
    </div>
  );
};

export default Terminal;

